import { memoriesOf } from 'helpers/memories';
import { useLayoutEffect, useRef, useState } from 'react';
import { AlbumContentType } from 'common/enums';
import { ContentThumbnail } from 'common/content-thumbnail';
import { MemberAvatar } from 'common/member-avatar';
import { classnames } from 'helpers/utils';
import { Album } from 'models/album';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import styles from './collection-card.module.scss';

interface CollectionCardProps {
  album: Album;
  onClick?: () => void;
  // When set (Shared With Me view), shows who shared this album, bottom-right.
  sharedBy?: { name: string };
  // Drop support — a memory dragged from the grid can be copied onto this album.
  onDragOver?: (e: React.DragEvent<HTMLButtonElement>) => void;
  onDragLeave?: (e: React.DragEvent<HTMLButtonElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLButtonElement>) => void;
  // A drag is in flight and this album is a valid target (doesn't have it yet).
  isDropActive?: boolean;
  // The dragged memory is hovering over this album right now.
  isDropTarget?: boolean;
  // This album already has the dragged memory, so it can't be dropped here.
  isDropDisabled?: boolean;
}

// Up to 3 contents for the fanned thumbnail stack. Visual content (images,
// videos, or anything with a server thumbnail) leads so it lands on the front
// tile; notes/audio become icon tiles. Each tile is rendered with the shared
// ContentThumbnail so the logic matches the memory cards and album grid.
const getStackContents = (album: Album): AlbumContentSerialized[] => {
  const contents = memoriesOf(album);
  const isVisual = (content: AlbumContentSerialized) =>
    content.contentType === AlbumContentType.image ||
    content.contentType === AlbumContentType.video ||
    !!content.thumbnailUrl;
  const visual = contents.filter(isVisual);
  const rest = contents.filter((content) => !isVisual(content));
  return [...visual, ...rest].slice(0, 3);
};

export const CollectionCard = ({
  album,
  onClick,
  sharedBy,
  onDragOver,
  onDragLeave,
  onDrop,
  isDropActive,
  isDropTarget,
  isDropDisabled,
}: CollectionCardProps) => {
  const stack = getStackContents(album);
  const count = memoriesOf(album).length;

  // Only titles that actually overflow one line get the two-line hover
  // treatment, and CSS can't detect overflow — so measure it.
  const titleRef = useRef<HTMLSpanElement>(null);
  const isHoveredRef = useRef(false);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return undefined;

    // Skip while hovered: the title is wrapped to two lines then, so it would
    // measure as fitting, drop the class, snap back to one line and re-measure
    // as overflowing — a flicker loop.
    const measure = () => {
      if (isHoveredRef.current) return;
      setIsTitleTruncated(el.scrollWidth > el.clientWidth);
    };

    measure();
    // Re-measure when the card resizes or the webfont finishes swapping in,
    // either of which changes whether the title fits.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [album.name]);
  // Always render 3 fanned tiles when the album has content (cycle to fill).
  const tiles =
    stack.length > 0 ? [0, 1, 2].map((i) => stack[i % stack.length]) : [];

  return (
    <button
      className={classnames(styles.card, {
        [styles.dropActive]: !!isDropActive,
        [styles.dropTarget]: !!isDropTarget,
        [styles.dropDisabled]: !!isDropDisabled,
        [styles.titleTruncated]: isTitleTruncated,
      })}
      onClick={onClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      // Where the returning-customer introduction lands its pictures.
      data-album-id={album.id}
      onMouseEnter={() => {
        isHoveredRef.current = true;
      }}
      onMouseLeave={() => {
        isHoveredRef.current = false;
      }}
      type="button"
    >
      {/* An empty album keeps the same three-tile fan, just with blank cards
          instead of thumbnails. */}
      <div className={styles.thumbnailWrapper}>
        <div className={styles.stack}>
          <div className={`${styles.tile} ${styles.tileBack}`}>
            {tiles.length > 0 && (
              <ContentThumbnail
                content={tiles[2]}
                className={styles.tileThumb}
              />
            )}
          </div>
          <div className={`${styles.tile} ${styles.tileMid}`}>
            {tiles.length > 0 && (
              <ContentThumbnail
                content={tiles[1]}
                className={styles.tileThumb}
              />
            )}
          </div>
          <div
            className={`${styles.tile} ${styles.tileFront}`}
            data-handoff-album
          >
            {tiles.length > 0 && (
              <ContentThumbnail
                content={tiles[0]}
                className={styles.tileThumb}
              />
            )}
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.footerText}>
          <span className={styles.title} ref={titleRef}>
            {album.name}
          </span>
          <span className={styles.count}>
            {count} {count === 1 ? 'Memory' : 'Memories'}
          </span>
        </div>
        {sharedBy && (
          <MemberAvatar
            name={sharedBy.name}
            size={24}
            className={styles.sharerAvatar}
          />
        )}
      </div>
    </button>
  );
};
